import React from 'react';
import { Result, Button, Typography, Alert, Space } from 'antd';
import { 
  ReloadOutlined, 
  HomeOutlined, 
  ApiOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  DisconnectOutlined 
} from '@ant-design/icons';
import { getUserFriendlyMessage } from '../../../utils/errorHandler';
import './ApiErrorPage.css';

const { Text, Paragraph } = Typography;

const ApiErrorPage = ({ 
  error, 
  onRetry, 
  onGoHome,
  showRetry = true,
  showHome = true,
  level = 'page' 
}) => {
  const getErrorStatus = (error) => {
    if (!error) return '500';
    
    if (error.isStatus?.(404)) return '404';
    if (error.isServerError?.()) return '500';
    
    return 'error';
  };

  const getErrorIcon = (error) => {
    if (!error) return null;
    
    if (error.isNetworkError?.()) return <DisconnectOutlined />;
    if (error.isStatus?.(401)) return <ExclamationCircleOutlined />;
    if (error.isStatus?.(403)) return <ExclamationCircleOutlined />;
    if (error.isStatus?.(429)) return <ClockCircleOutlined />;
    if (error.isServerError?.()) return null; // Use default for server errors
    
    return <ApiOutlined />;
  };

  const getErrorTitle = (error) => {
    if (!error) return 'API Error';
    
    if (error.isNetworkError?.()) return 'Connection Problem';
    if (error.isStatus?.(401)) return 'Authentication Required';
    if (error.isStatus?.(403)) return 'Access Denied';
    if (error.isStatus?.(404)) return 'Not Found';
    if (error.isStatus?.(429)) return 'Too Many Requests';
    if (error.isServerError?.()) return 'Server Error';
    if (error.isClientError?.()) return 'Request Error';
    
    return 'API Error';
  };

  const getErrorSubtitle = (error) => {
    const userFriendlyMessage = getUserFriendlyMessage(error);
    
    if (!error) {
      return 'An unexpected error occurred while communicating with the server.';
    }
    
    return userFriendlyMessage;
  };

  const getStatusDescription = (error) => {
    if (!error?.status) return null;
    
    const descriptions = {
      401: 'Your session may have expired. Please log in again.',
      403: 'You don\'t have permission to access this resource.',
      404: 'The requested resource could not be found.',
      422: 'The request contains invalid data.',
      429: 'You\'ve made too many requests. Please wait before trying again.',
      500: 'The server encountered an internal error.',
      502: 'Bad gateway - the server received an invalid response.',
      503: 'The service is temporarily unavailable.',
      504: 'The server took too long to respond.'
    };
    
    return descriptions[error.status];
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = '/';
    }
  };

  // Component-level error display (smaller, inline)
  if (level === 'component') {
    return (
      <div className="api-error-page api-error-page--component">
        <Alert
          message={getErrorTitle(error)}
          description={getErrorSubtitle(error)}
          type="error"
          showIcon
          action={
            showRetry && (
              <Button size="small" type="primary" onClick={handleRetry}>
                Retry
              </Button>
            )
          }
        />
      </div>
    );
  }

  // Page-level error display (full page)
  const actions = [];
  
  if (showRetry) {
    actions.push(
      <Button 
        type="primary" 
        icon={<ReloadOutlined />}
        onClick={handleRetry}
        key="retry"
      >
        Try Again
      </Button>
    );
  }
  
  if (showHome) {
    actions.push(
      <Button 
        icon={<HomeOutlined />}
        onClick={handleGoHome}
        key="home"
      >
        Go Home
      </Button>
    );
  }

  const customIcon = getErrorIcon(error);

  return (
    <div className="api-error-page api-error-page--page">
      <Result
        status={getErrorStatus(error)}
        icon={customIcon}
        title={getErrorTitle(error)}
        subTitle={getErrorSubtitle(error)}
        extra={actions}
      />
      
      {/* Additional error details */}
      {error && (
        <div className="api-error-page__details">
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {/* Status code and description */}
            {error.status && (
              <div className="api-error-page__status">
                <Text type="secondary">
                  <strong>Status:</strong> {error.status}
                </Text>
                {getStatusDescription(error) && (
                  <Paragraph type="secondary" style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                    {getStatusDescription(error)}
                  </Paragraph>
                )}
              </div>
            )}
            
            {/* Timestamp */}
            {error.timestamp && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <strong>Time:</strong> {new Date(error.timestamp).toLocaleString()}
              </Text>
            )}
            
            {/* Validation errors */}
            {error.details?.validationErrors && error.details.validationErrors.length > 0 && (
              <div className="api-error-page__validation">
                <Text type="secondary" strong>Validation Errors:</Text>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  {error.details.validationErrors.map((validationError, index) => (
                    <li key={index} style={{ fontSize: '14px', color: '#8c8c8c' }}>
                      <strong>{validationError.field}:</strong> {validationError.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Retry after for rate limiting */}
            {error.details?.retryAfter && (
              <Alert
                message={`Please wait ${error.details.retryAfter} seconds before trying again.`}
                type="warning"
                size="small"
                showIcon
              />
            )}
          </Space>
        </div>
      )}
      
      {/* Development error details */}
      {process.env.NODE_ENV === 'development' && error && (
        <details className="api-error-page__debug">
          <summary>Debug Information (Development Only)</summary>
          <div className="api-error-page__debug-content">
            <h4>Error Object:</h4>
            <pre>{JSON.stringify({
              name: error.name,
              message: error.message,
              status: error.status,
              details: error.details,
              timestamp: error.timestamp
            }, null, 2)}</pre>
            
            {error.originalError && (
              <>
                <h4>Original Error:</h4>
                <pre>{error.originalError.toString()}</pre>
                
                {error.originalError.config && (
                  <>
                    <h4>Request Details:</h4>
                    <pre>{JSON.stringify({
                      method: error.originalError.config.method,
                      url: error.originalError.config.url,
                      baseURL: error.originalError.config.baseURL,
                      headers: error.originalError.config.headers
                    }, null, 2)}</pre>
                  </>
                )}
              </>
            )}
          </div>
        </details>
      )}
    </div>
  );
};

export default ApiErrorPage;